// frontend/src/pages/AccountingTypes/EditAccountingType.jsx
import React, { useMemo, useState } from "react";
import { Lock, RefreshCw, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

import { useAuth } from "../../contexts/AuthContext";
import "./AccountingType.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (
      typeof company.currency === "string" &&
      /^[A-Z]{3}$/.test(company.currency)
    ) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (
      typeof company.currency_code === "string" &&
      /^[A-Z]{3}$/.test(company.currency_code)
    ) {
      currency = company.currency_code;
    } else if (
      typeof company.currency_code === "object" &&
      company.currency_code.code
    ) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (
      typeof company.base_currency === "string" &&
      /^[A-Z]{3}$/.test(company.base_currency)
    ) {
      currency = company.base_currency;
    } else if (
      typeof company.base_currency === "object" &&
      company.base_currency.code
    ) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id || value.id || "";
  return value;
};

const EditAccountingType = () => {
  const navigate = useNavigate();
  const { currentUser, userType, company } = useAuth();

  const isSystemAdmin =
    userType === "system" &&
    ["developer", "admin"].includes(currentUser?.role);

  const companyId =
    userType === "company"
      ? getId(company) || getId(currentUser?.company_id)
      : "";

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [submitting, setSubmitting] = useState(false);

  const installSetup = async () => {
    if (!companyId) {
      toast.error("No company context found. Please log in as a company user.");
      return;
    }

    const confirmed = window.confirm(
      `Install/repair the protected inventory setup for your company?\n\n` +
      `This will align every protected account (and its existing subaccounts) ` +
      `to ${companyCurrency}.`,
    );

    if (!confirmed) return;

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${url}/api/account/setup-inventory`,
        {
          company_id: companyId,
          cleanup_legacy: true,
          force_currency_update: true,
          currency_code: companyCurrency,
        },
        { headers: getAuthHeaders() },
      );

      toast.success(
        response.data?.message || "Inventory accounting setup installed",
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to install inventory accounting setup",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="accounting-type-page">
      <div className="accounting-page-shell">
        <section className="standard-setup-panel">
          <div>
            <span className="panel-eyebrow">Protected configuration</span>
            <h2>
              <Lock size={20} /> Accounting types cannot be edited
            </h2>
            <p>
              The Inventory Management System uses eight fixed accounting
              classifications. Create or edit a Chart of Accounts subaccount
              instead.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {/* ⭐ Optional: allow a one-click repair from this page */}
            {companyId ? (
              <button
                type="button"
                className="create-standard-btn"
                onClick={installSetup}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Installing…
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} /> Install / Repair Setup
                  </>
                )}
              </button>
            ) : null}

            <button
              type="button"
              className="create-standard-btn"
              onClick={() => navigate("/ViewAccountingTypes")}
            >
              Back to Accounting Types
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EditAccountingType;